import { useCallback, useMemo, useState } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  ActionLink,
  ActionLinkKey,
  ButtonWrap,
  Divider,
  type IActionLink,
  IconComponent,
  IconName,
  MobilePopoverCard,
} from "@hopper-b2b/ui";
import { DialogContent, DialogTitle, Typography } from "@material-ui/core";
import clsx from "clsx";
import ArrowAction from "../../../assets/client/arrow-action.svg?react";
import Cancel from "../../../assets/client/cancel.svg?react";
import CheckCircle from "../../../assets/client/check-circle.svg?react";
import Headset from "../../../assets/client/headset.svg?react";
import Mail from "../../../assets/client/mail.svg?react";
import MoreOptionsIcon from "../../../assets/client/more.svg?react";
import Pencil from "../../../assets/client/pencil.svg?react";
import "./styles.scss";

const MORE_OPTIONS_THRESHOLD = 4;

interface IMobileActionLinksProps {
  actions: IActionLink[];
}

export const MobileActionLinks = ({ actions }: IMobileActionLinksProps) => {
  const { t } = useI18nContext();

  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);

  const getIcon = useCallback((key: ActionLinkKey) => {
    switch (key) {
      case ActionLinkKey.CHECK_IN:
        return <CheckCircle className="mobile-action-link-icon check-in" />;
      case ActionLinkKey.EXCHANGE:
        return <Pencil className="mobile-action-link-icon exchange" />;
      case ActionLinkKey.CFAR:
      case ActionLinkKey.CANCELLATION:
        return <Cancel className="mobile-action-link-icon cancellation" />;
      case ActionLinkKey.RESEND_CONFIRMATION:
        return <Mail className="mobile-action-link-icon resend-confirmation" />;
      case ActionLinkKey.HELP:
        return <Headset className="mobile-action-link-icon help" />;
    }
  }, []);

  const hasOverflow = actions.length > MORE_OPTIONS_THRESHOLD;

  const { visibleActions, overflowActions } = useMemo(() => {
    if (!hasOverflow) {
      return { visibleActions: actions, overflowActions: [] };
    }
    return {
      visibleActions: actions.slice(0, MORE_OPTIONS_THRESHOLD - 1),
      overflowActions: actions.slice(MORE_OPTIONS_THRESHOLD - 1),
    };
  }, [actions, hasOverflow]);

  const handleOverflowActionClick = (action: IActionLink) => {
    setMoreOptionsOpen(false);
    action.onClick();
  };

  const renderActionButton = (
    action: IActionLink,
    index: number,
    total: number
  ) => (
    <div
      className="mobile-action-link-button-container"
      key={`${action.key}-link-button-${index}`}
    >
      <ButtonWrap
        className={clsx("mobile-action-link", {
          "link-separator": index !== total - 1,
        })}
        onClick={action.onClick}
      >
        {getIcon(action.key)}
      </ButtonWrap>
      <Typography
        className={clsx("mobile-action-link-text", action.linkClassName)}
      >
        {action.content}
      </Typography>
    </div>
  );

  const totalVisible = hasOverflow ? visibleActions.length + 1 : actions.length;

  return (
    <div className="mobile-action-link-root">
      <div className="mobile-action-links-container slot">
        {visibleActions.map((action, index) =>
          renderActionButton(action, index, totalVisible)
        )}
        {hasOverflow && (
          <div
            className="mobile-action-link-button-container"
            key="more-options-link-button"
          >
            <ButtonWrap
              className="mobile-action-link"
              onClick={() => setMoreOptionsOpen(true)}
            >
              <MoreOptionsIcon className="mobile-action-link-icon more-options" />
            </ButtonWrap>
            <Typography className="mobile-action-link-text">
              {t("moreOptions")}
            </Typography>
          </div>
        )}
      </div>

      <MobilePopoverCard
        open={moreOptionsOpen}
        className="more-options-modal"
        fullScreen
        onClose={() => setMoreOptionsOpen(false)}
        topRightButton={
          <ActionLink
            onClick={() => setMoreOptionsOpen(false)}
            content={
              <IconComponent
                ariaLabel={t("modalClose.ariaLabel")}
                className="close-button-icon"
                name={IconName.Close}
              />
            }
          />
        }
      >
        <DialogTitle className="more-options-modal-title" disableTypography>
          <h2 className="title">{t("moreOptions")}</h2>
        </DialogTitle>
        <DialogContent className="more-options-modal-content">
          {overflowActions.map((action, index) => (
            <div key={`overflow-action-${action.key ?? index}`}>
              <ButtonWrap
                className="more-options-action-row"
                onClick={() => handleOverflowActionClick(action)}
              >
                <div className="more-options-action-row-left">
                  {getIcon(action.key)}
                  <p className="more-options-action-row-title">
                    {action.content}
                  </p>
                </div>
                <ArrowAction className="more-options-action-row-chevron" />
              </ButtonWrap>
              {index < overflowActions.length - 1 && <Divider />}
            </div>
          ))}
        </DialogContent>
      </MobilePopoverCard>
    </div>
  );
};
